import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Heart, Minus, Plus, Package, FlaskConical, Shield, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
        setSelectedImage(0);

        if (isAuthenticated) {
          const wishlistRes = await axios.get(`${API}/wishlist`);
          const wishlistIds = wishlistRes.data.products?.map(p => p.id) || [];
          setIsInWishlist(wishlistIds.includes(id));
        }
      } catch (error) {
        console.error('Failed to fetch product:', error);
        toast.error('Product not found');
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, isAuthenticated, navigate]);

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to add items to cart');
      navigate('/login');
      return;
    }

    const success = await addToCart(product.id, quantity);
    if (success) {
      toast.success(`${product.name} added to cart`);
    } else {
      toast.error('Failed to add to cart');
    }
  };

  const handleWishlistToggle = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to save items');
      return;
    }

    try {
      if (isInWishlist) {
        await axios.delete(`${API}/wishlist/remove/${id}`);
        setIsInWishlist(false);
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/wishlist/add`, { product_id: id });
        setIsInWishlist(true);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error('Failed to update wishlist');
    }
  };

  const getImages = () => {
    if (product?.gallery && product.gallery.length > 0) {
      return product.gallery;
    }
    return [product?.image_url];
  };

  const nextImage = () => {
    const images = getImages();
    setSelectedImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    const images = getImages();
    setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F2E8] pt-24 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-white skeleton"></div>
            <div className="space-y-4">
              <div className="h-8 w-24 bg-white skeleton"></div>
              <div className="h-12 w-3/4 bg-white skeleton"></div>
              <div className="h-24 w-full bg-white skeleton"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const images = getImages();
  const hasGallery = images.length > 1;

  return (
    <div className="min-h-screen bg-[#F5F2E8] pt-24 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#5A5A5A] hover:text-[#1A1A1A] mb-8 transition-colors text-sm font-['IBM_Plex_Sans']"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          BACK TO CATALOG
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Product Images */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="relative aspect-square overflow-hidden bg-white border-2 border-[#E0DDD4]">
              <img 
                src={images[selectedImage]} 
                alt={product.name}
                className="w-full h-full object-cover"
              />
              
              {/* Category Badge */}
              <span className={`absolute top-4 left-4 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] font-['IBM_Plex_Sans_Condensed'] ${
                product.category === 'food' 
                  ? 'bg-white text-[#1A1A1A] border border-[#E0DDD4]' 
                  : 'bg-[#1A1A1A] text-white'
              }`}>
                {product.category === 'food' ? 'NUTRITION' : 'EQUIPMENT'}
              </span>

              {/* Gallery Navigation */}
              {hasGallery && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 flex items-center justify-center hover:bg-white transition-colors border border-[#E0DDD4]"
                    data-testid="prev-image"
                  >
                    <ChevronLeft className="w-5 h-5 text-[#1A1A1A]" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 flex items-center justify-center hover:bg-white transition-colors border border-[#E0DDD4]"
                    data-testid="next-image"
                  >
                    <ChevronRight className="w-5 h-5 text-[#1A1A1A]" />
                  </button>
                  {/* Image Counter */}
                  <div className="absolute top-4 right-4 bg-[#1A1A1A] text-white text-xs px-3 py-1 font-['IBM_Plex_Mono']">
                    {selectedImage + 1} / {images.length}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnail Gallery */}
            {hasGallery && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`shrink-0 w-20 h-20 overflow-hidden border-2 transition-all ${
                      selectedImage === index 
                        ? 'border-[#4A7C29]' 
                        : 'border-[#E0DDD4] hover:border-[#5A5A5A]'
                    }`}
                    data-testid={`thumbnail-${index}`}
                  >
                    <img 
                      src={img} 
                      alt={`${product.name} view ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="flex flex-col">
            <div className="flex-1">
              <p className="text-[#4A7C29] text-[10px] font-semibold tracking-[0.2em] mb-2 font-['IBM_Plex_Sans_Condensed'] uppercase">
                {product.category === 'food' ? 'TJGI — NUTRITION SYSTEMS' : 'TJGI — EQUIPMENT DIVISION'}
              </p>
              <h1 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-4 font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">
                {product.name}
              </h1>
              
              <p className="text-[#5A5A5A] mb-6 leading-relaxed font-['IBM_Plex_Sans']">
                {product.description}
              </p>

              {/* Price & Weight */}
              <div className="flex items-center gap-6 mb-8">
                <span className="text-2xl font-semibold text-[#1A1A1A] font-['IBM_Plex_Sans']">
                  ${product.price.toFixed(2)}
                </span>
                {product.weight && (
                  <span className="flex items-center gap-2 text-[#5A5A5A] bg-white px-3 py-2 border-2 border-[#E0DDD4] font-['IBM_Plex_Mono'] text-sm">
                    {product.weight}
                  </span>
                )}
              </div>

              {/* Features */}
              <div className="grid grid-cols-3 gap-3 mb-8">
                <div className="bg-white p-4 text-center border-2 border-[#E0DDD4]">
                  <Package className="w-5 h-5 text-[#4A7C29] mx-auto mb-2" />
                  <span className="text-[10px] text-[#5A5A5A] font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">FREE SHIPPING</span>
                </div>
                <div className="bg-white p-4 text-center border-2 border-[#E0DDD4]">
                  <Shield className="w-5 h-5 text-[#4A7C29] mx-auto mb-2" />
                  <span className="text-[10px] text-[#5A5A5A] font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">LAB TESTED</span>
                </div>
                <div className="bg-white p-4 text-center border-2 border-[#E0DDD4]">
                  <FlaskConical className="w-5 h-5 text-[#4A7C29] mx-auto mb-2" />
                  <span className="text-[10px] text-[#5A5A5A] font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">R&D CERTIFIED</span>
                </div>
              </div>

              {/* Stock Status */}
              <p className="text-sm text-[#5A5A5A] mb-6 font-['IBM_Plex_Sans']">
                <span className="text-[#4A7C29]">● IN STOCK</span>
                <span className="ml-2">— Ships within 24 hours</span>
              </p>
            </div>

            {/* Quantity & Add to Cart */}
            <div className="border-t-2 border-[#E0DDD4] pt-6">
              <div className="flex items-center gap-6 mb-6">
                <span className="text-[#5A5A5A] text-xs font-medium font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">QUANTITY:</span>
                <div className="flex items-center gap-2 bg-white border-2 border-[#E0DDD4] p-1">
                  <button 
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-9 h-9 flex items-center justify-center hover:bg-[#F5F2E8] transition-colors"
                    data-testid="quantity-decrease"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-10 text-center font-semibold text-[#1A1A1A] font-['IBM_Plex_Mono']" data-testid="quantity-value">
                    {quantity}
                  </span>
                  <button 
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-9 h-9 flex items-center justify-center hover:bg-[#F5F2E8] transition-colors"
                    data-testid="quantity-increase"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  onClick={handleAddToCart}
                  className="flex-1 rounded-none bg-[#4A7C29] text-white font-semibold py-6 text-sm hover:bg-[#3D6822] transition-colors font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wider"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingBag className="w-5 h-5 mr-2" />
                  ADD TO CART
                </Button>
                <Button 
                  onClick={handleWishlistToggle}
                  variant="outline"
                  className={`rounded-none p-6 border-2 transition-colors ${
                    isInWishlist 
                      ? 'bg-[#4A7C29] border-[#4A7C29] text-white' 
                      : 'border-[#E0DDD4] text-[#1A1A1A] hover:bg-[#F5F2E8] hover:border-[#4A7C29]'
                  }`}
                  data-testid="wishlist-btn"
                >
                  <Heart className={`w-5 h-5 ${isInWishlist ? 'fill-current' : ''}`} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
